import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErr, fmtDate } from "@/lib/format";
import { useCompany } from "@/contexts/CompanyContext";
import NewCaiModal from "@/pages/_settings/NewCaiModal";
import CaiSwitch from "@/pages/_settings/CaiSwitch";
import {
  ReceiptIcon, TruckIcon, ArrowCircleDownIcon, ArrowCircleUpIcon, FileTextIcon, XIcon, PlusIcon,
  FloppyDiskIcon, ShieldCheckIcon, NotePencilIcon,
} from "@phosphor-icons/react";
import { useModalForm } from "@/lib/modalForm";
import BotonCerrar from "@/components/BotonCerrar";

/**
 * Configuración por documento (Configuración de documentos y datos fiscales).
 * - Un acceso por documento: Factura, Guía de Remisión, Nota de Crédito, Nota de Débito y Proforma.
 * - Datos específicos del documento (nota, dirección/teléfono propios, condiciones de proforma…).
 * - Autorizaciones fiscales (CAI) separadas por documento, con vigencia programable e historial.
 * Los documentos ya emitidos conservan los datos y la nota con que se generaron.
 */
const ICONS = {
  factura: ReceiptIcon, guia_remision: TruckIcon, nota_credito: ArrowCircleDownIcon,
  nota_debito: ArrowCircleUpIcon, proforma: FileTextIcon,
};
const ESTADO = {
  vigente: "bg-agro-green/15 text-agro-green border-agro-green/40",
  programada: "bg-agro-yellow/15 text-agro-yellow border-agro-yellow/40",
  vencida: "bg-red-500/15 text-red-300 border-red-500/40",
  agotada: "bg-red-500/15 text-red-300 border-red-500/40",
  inhabilitada: "bg-text-muted/15 text-text-muted border-text-muted/40",
  historica: "bg-text-muted/15 text-text-muted border-text-muted/40",
};

export default function DocumentConfigSection({ comun, reloadKey = 0, onChanged, openRequest, branch, canEdit = true }) {
  const [docs, setDocs] = useState([]);
  const [open, setOpen] = useState(null);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/settings/documents", { params: { branch_id: branch.id } });
      setDocs(data.documentos || []);
    } catch (e) { toast.error(apiErr(e)); }
  }, [branch.id]);
  useEffect(() => { load(); }, [load, reloadKey]);
  useEffect(() => {
    if (!openRequest) return;
    api.get("/settings/documents", { params: { branch_id: branch.id } }).then(({ data }) => {
      const d = (data.documentos || []).find((x) => x.tipo === openRequest.tipo);
      if (d) setOpen(d);
    }).catch(() => {});
  }, [openRequest, branch.id]);

  return (
    <div className="bg-card border border-agro-green/30 rounded-lg overflow-hidden" data-testid="doc-config-section">
      <div className="px-5 py-3 border-b border-agro-green/20 bg-agro-green/5 flex gap-3 items-start">
        <ShieldCheckIcon size={20} className="text-agro-green" weight="duotone" />
        <div>
          <div className="font-heading text-agro-green">Configuración por documento · {branch.nombre}</div>
          <div className="text-[11px] text-text-muted mt-0.5">
            Lo propio de cada documento: su autorización fiscal (CAI, rango, correlativo y vigencia), la nota
            impresa y sus datos específicos. Los diseños de Gestión de Plantillas toman estos datos.
          </div>
        </div>
      </div>
      <div className="p-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {docs.map((d) => {
          const Icon = ICONS[d.tipo] || FileTextIcon;
          const act = d.autorizacion_vigente;
          return (
            <button key={d.tipo} type="button" onClick={() => setOpen(d)} data-testid={`doc-config-${d.tipo}`}
              className="text-left bg-app border border-border rounded-lg p-4 hover:border-agro-green/60 transition-colors">
              <div className="flex items-center gap-2 mb-2">
                <Icon size={20} weight="duotone" className="text-agro-green" />
                <span className="font-semibold text-sm">{d.label}</span>
                <span className="ml-auto text-[10px] uppercase tracking-wider text-agro-green">Configurar</span>
              </div>
              {d.fiscal ? (
                act ? (
                  <div className="text-[11px] text-text-secondary space-y-0.5">
                    <div className="font-mono truncate" title={act.numero_cai}>CAI {act.numero_cai}</div>
                    <div>Próximo: <span className="font-mono">{String(act.correlativo_actual).padStart(8, "0")}</span> · restan {act.documentos_restantes}</div>
                    <div>Vence {fmtDate(act.fecha_limite)}</div>
                  </div>
                ) : <div className="text-[11px] text-red-300">Sin autorización vigente</div>
              ) : (
                <div className="text-[11px] text-text-secondary">Numeración interna independiente · próximo {d.proximo_numero}</div>
              )}
              {(d.programadas || []).length > 0 && (
                <div className="mt-1 text-[11px] text-agro-yellow">Programada desde {fmtDate(d.programadas[0].fecha_inicio)}</div>
              )}
              {d.tiene_nota && <div className="mt-1 text-[11px] text-text-muted">Nota del documento configurada</div>}
            </button>
          );
        })}
      </div>
      {open && <DocumentConfigModal doc={open} comun={comun} branch={branch} canEdit={canEdit} onClose={() => setOpen(null)}
                                    onSaved={() => { load(); onChanged?.(); }} />}
    </div>
  );
}

function DocumentConfigModal({ doc, comun, branch, canEdit, onClose, onSaved }) {
  const { branches } = useCompany();
  const [cfg, setCfg] = useState({ ...(doc.config || {}) });
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [newCai, setNewCai] = useState(false);
  const [editCai, setEditCai] = useState(null);
  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const loadHistory = useCallback(async () => {
    if (!doc.fiscal) return;
    try {
      const { data } = await api.get("/cai-configurations", { params: { tipo_documento: doc.tipo, branch_id: branch.id } });
      setHistory(data || []);
    } catch (e) { toast.error(apiErr(e)); }
  }, [doc, branch.id]);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...cfg };
      if (doc.tipo === "proforma") payload.vigencia_dias = Number(cfg.vigencia_dias || 0);
      await api.put(`/settings/branches/${branch.id}/documentos/${doc.tipo}`, payload);
      toast.success(`${doc.label} de ${branch.nombre} guardada. Los documentos ya emitidos no cambian.`);
      onSaved?.();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };


  const inp = "w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green outline-none";
  const dc = useModalForm({ value: cfg, onClose: onClose, onSave: save });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
         data-testid="doc-config-modal" {...dc.overlayProps}>
      <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-border/60">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-agro-green" data-testid="doc-config-context">
              {comun?.marca || "AGROPORT"} › {branch.nombre} · Configuración del documento
            </div>
            <h2 className="font-heading text-xl">{doc.label}</h2>
          </div>
          <button aria-label="Cerrar" onClick={dc.cerrar} className="text-text-muted hover:text-text-primary"><XIcon size={22} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          {/* ---------- Autorizaciones fiscales ---------- */}
          {doc.fiscal && (
            <section>
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheckIcon size={18} className="text-agro-green" weight="duotone" />
                <h3 className="font-semibold text-sm">Autorizaciones fiscales (CAI)</h3>
                <button type="button" onClick={() => setNewCai(true)} data-testid="doc-new-cai" disabled={!canEdit}
                  className="ml-auto flex items-center gap-1 text-xs font-semibold border border-agro-green/50 text-agro-green rounded-md px-3 py-1.5 hover:bg-agro-green/10">
                  <PlusIcon size={14} /> Registrar nueva autorización
                </button>
              </div>
              <p className="text-[11px] text-text-muted mb-3">
                Puedes registrarla con anticipación indicando desde qué fecha se usará: el sistema la aplica ese día
                automáticamente, evita vigencias superpuestas y correlativos duplicados y bloquea la emisión con el
                rango agotado o vencido. Sin límite de cambios por año.
              </p>
              <div className="border border-border rounded-md overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-sidebar text-[10px] uppercase tracking-wider text-text-muted">
                    <tr><th className="p-2 text-left">Sucursal</th><th className="p-2 text-left">CAI</th><th className="p-2 text-left">Rango autorizado</th>
                      <th className="p-2 text-left">Desde</th><th className="p-2 text-left">Límite</th>
                      <th className="p-2 text-right">Próximo</th><th className="p-2 text-right">Restan</th>
                      <th className="p-2 text-center">Estado</th><th className="p-2"></th></tr>
                  </thead>
                  <tbody>
                    {history.length === 0 && <tr><td colSpan={9} className="p-4 text-center text-text-muted">Sin autorizaciones registradas</td></tr>}
                    {history.map((c) => (
                      <tr key={c.id} className="border-t border-border/40" data-testid={`cai-row-${c.estado}`}>
                        <td className="p-2 text-[11px] text-text-secondary">{c.branch_nombre}</td>
                        <td className="p-2 font-mono text-[11px]">{c.numero_cai}</td>
                        <td className="p-2 font-mono text-[11px]">{c.rango_inicial}<br />{c.rango_final}</td>
                        <td className="p-2" title={c.entra_en_vigor}>{fmtDate(c.fecha_inicio || c.fecha_activacion)}
                          {c.se_inhabilita && <div className="text-[10px] text-agro-yellow">Se inhabilita {c.se_inhabilita}</div>}</td>
                        <td className="p-2">{fmtDate(c.fecha_limite)}</td>
                        <td className="p-2 text-right font-mono">{String(c.correlativo_actual).padStart(8, "0")}
                          {c.inicio_programado?.numero && <div className="text-[10px] text-blue-300 font-sans">desde {fmtDate(c.inicio_programado.fecha)}: {String(c.inicio_programado.numero).padStart(8, "0")}</div>}</td>
                        <td className="p-2 text-right font-mono">{c.documentos_restantes}</td>
                        <td className="p-2 text-center">
                          <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded border ${ESTADO[c.estado] || ESTADO.historica}`}>{c.estado}</span>
                        </td>
                        <td className="p-2 text-right">
                          <div className="inline-flex items-center gap-2">
                            <button onClick={() => setEditCai(c)} disabled={!canEdit} data-testid={`doc-cai-edit-${c.id}`}
                              className="text-[11px] text-text-muted hover:text-agro-green disabled:opacity-40">EDITAR</button>
                            <CaiSwitch cai={c} disabled={!canEdit} onChanged={() => { loadHistory(); onSaved?.(); }} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* ---------- Nota del documento ---------- */}
          {doc.tiene_nota && (
            <section>
              <div className="flex items-center gap-2 mb-2">
                <NotePencilIcon size={18} className="text-agro-green" weight="duotone" />
                <h3 className="font-semibold text-sm">Nota del documento</h3>
              </div>
              <textarea rows={6} value={cfg.nota || ""} onChange={(e) => set("nota", e.target.value)} maxLength={1500}
                        data-testid="doc-nota" className={`${inp} resize-y leading-relaxed`} />
              <div className="text-[10px] text-text-muted mt-1">
                Se imprime en el recuadro "NOTA" del diseño institucional respetando los saltos de línea.
                Cada documento guarda la nota vigente al emitirse: cambiarla aquí no altera los documentos ya emitidos.
                ({(cfg.nota || "").length}/1500)
              </div>
            </section>
          )}

          {/* ---------- Datos específicos ---------- */}
          {doc.tipo === "guia_remision" && (
            <div className="text-[11px] text-text-muted bg-sidebar/40 rounded p-3" data-testid="guia-numeracion-interna">
              La guía de remisión usa numeración interna consecutiva (GR-000001…), como la proforma: no requiere
              autorización del SAR, CAI, rango ni fecha límite. Próximo número: <span className="font-mono">{doc.proximo_numero}</span>.
              Las guías emitidas antes con autorización conservan su número y sus datos.
            </div>
          )}
          <section>
            <h3 className="font-semibold text-sm mb-2">Datos específicos del documento</h3>
            {doc.tipo === "guia_remision" ? (
              <div className="grid md:grid-cols-2 gap-3">
                <Fld label="Nombre del remitente"><input value={cfg.remitente_nombre || ""} onChange={(e) => set("remitente_nombre", e.target.value)} placeholder={comun?.nombre_legal} className={inp} data-testid="input-remitente-nombre" /></Fld>
                <Fld label="RTN del remitente"><input value={cfg.remitente_rtn || ""} onChange={(e) => set("remitente_rtn", e.target.value)} placeholder={comun?.rtn} className={inp} data-testid="input-remitente-rtn" /></Fld>
                <Fld label="Dirección del remitente"><input value={cfg.remitente_direccion || ""} onChange={(e) => set("remitente_direccion", e.target.value)} placeholder={branch.direccion} className={inp} data-testid="input-remitente-direccion" /></Fld>
                <Fld label="Teléfono del remitente"><input value={cfg.remitente_telefono || ""} onChange={(e) => set("remitente_telefono", e.target.value)} placeholder={branch.telefono} className={inp} data-testid="input-remitente-telefono" /></Fld>
              </div>
            ) : (
              <div className="grid md:grid-cols-3 gap-3">
                <div className="md:col-span-2">
                  <Fld label="Dirección propia (opcional)" hint={doc.fiscal ? "Si se deja vacía se usa la dirección fiscal de la autorización CAI o la dirección de la sucursal." : "Si se deja vacía se usa la dirección de la sucursal."}>
                    <input value={cfg.direccion || ""} onChange={(e) => set("direccion", e.target.value)} placeholder={branch.direccion} className={inp} data-testid="doc-direccion" />
                  </Fld>
                </div>
                <Fld label="Teléfono propio (opcional)"><input value={cfg.telefono || ""} onChange={(e) => set("telefono", e.target.value)} placeholder={branch.telefono} className={inp} data-testid="doc-telefono" /></Fld>
              </div>
            )}
            {doc.tipo === "proforma" && (
              <div className="grid md:grid-cols-3 gap-3 mt-3">
                <Fld label="Vigencia de la oferta (días)" hint="La proforma muestra 'Válida hasta' = fecha + días.">
                  <input type="number" min={0} max={365} value={cfg.vigencia_dias ?? 15} onChange={(e) => set("vigencia_dias", e.target.value)} className={inp} data-testid="doc-vigencia" />
                </Fld>
                <div className="md:col-span-2">
                  <Fld label="Condiciones comerciales por defecto">
                    <textarea rows={3} value={cfg.condiciones || ""} onChange={(e) => set("condiciones", e.target.value)} className={`${inp} resize-y`} data-testid="doc-condiciones" />
                  </Fld>
                </div>
                <div className="md:col-span-3 text-[11px] text-text-muted bg-sidebar/40 rounded p-3">
                  La proforma usa numeración interna independiente (PRF-00000001…), no consume correlativos fiscales
                  ni muestra CAI. Al convertirla en factura, la factura toma la autorización y el correlativo vigentes
                  en ese momento.
                </div>
              </div>
            )}
          </section>
        </div>
        <div className="p-4 border-t border-border/60 flex items-center justify-between gap-2 bg-sidebar/30">
          <span className="text-[11px] text-text-muted">Los cambios aplican a los documentos que se emitan desde ahora.</span>
          <div className="flex gap-2">
            <button onClick={dc.cerrar} className="text-xs text-text-muted hover:text-text-primary px-3 py-2">Cerrar</button>
            <button onClick={dc.guardar} disabled={(saving || !canEdit) || dc.saving} data-testid="doc-config-save"
              className="bg-agro-green hover:bg-agro-green-hover disabled:opacity-40 text-black font-semibold rounded-md px-4 py-2 text-sm flex items-center gap-2">
              <FloppyDiskIcon size={16} weight="bold" /> {saving ? "Guardando…" : `Guardar ${doc.label}`}
            </button>
          </div>
        </div>
      </div>
      {editCai && (
        <NewCaiModal editing={editCai} defaults={{}} branches={branches || []} onClose={() => setEditCai(null)}
          onSaved={() => { setEditCai(null); loadHistory(); onSaved?.(); }} />
      )}
      {newCai && (
        <NewCaiModal lockTipo branches={branches || []} lockBranch={branch}
          defaults={{ tipo_documento: doc.tipo, direccion_fiscal: cfg.direccion || branch.direccion || "" }}
          onClose={() => setNewCai(false)}
          onSaved={() => { setNewCai(false); loadHistory(); onSaved?.(); }} />
      )}
    </div>
  );
}

function Fld({ label, hint, children }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-wider text-text-muted mb-1.5 font-medium">{label}</label>
      {children}
      {hint && <div className="text-[10px] text-text-muted/80 mt-1">{hint}</div>}
    </div>
  );
}
